import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog, DialogContent, Box, Typography, LinearProgress,
  Button, TextField, Grid, FormControl, FormLabel, RadioGroup,
  FormControlLabel, Radio, Checkbox, Divider, IconButton,
  Chip, Alert, InputAdornment, Select, MenuItem, InputLabel,
  Paper, Fade, CircularProgress, Tooltip,
  Stepper, Step, StepLabel, StepContent as MuiStepContent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GavelIcon from '@mui/icons-material/Gavel';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LockIcon from '@mui/icons-material/Lock';
import VerificadorDocumento from './VerificadorDocumento';
import TurnstileWidget      from './TurnstileWidget';
import DrawIcon from '@mui/icons-material/Draw';
import MyLocationIcon from '@mui/icons-material/MyLocation';

import {
  getPaises, getDepartamentos, getMunicipios,
  verificarEmail, verificarDocumento, geocodificarDireccion, registrarAfiliacion,
  getCooperativasPublicas,
} from '../services/ecolabApi';
import { descargarContratoPDF } from '../utils/generarContratoPDF';
import { descargarContratoCapellanesPDF } from '../utils/generarContratoCapellanesPDF';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const SectionText = ({ children, sx = {} }) => (
  <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.8, fontSize: '0.82rem', textAlign: 'justify', mb: 1.5, ...sx }}>
    {children}
  </Typography>
);

const ClausulaTitle = ({ number, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, mt: 3 }}>
    <Chip label={number} size="small" sx={{ background: 'linear-gradient(135deg, #2e7d32, #43a047)', color: '#fff', fontWeight: 700, fontSize: '0.7rem', height: 24 }} />
    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a3a1a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</Typography>
  </Box>
);

const ReadSection = ({ title, children, onRead, read }) => (
  <Paper elevation={0} sx={{ border: read ? '1.5px solid #43a047' : '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 2, background: read ? 'rgba(67,160,71,0.04)' : '#fafafa', transition: 'all 0.3s ease' }}>
    {title && <ClausulaTitle {...title} />}
    {children}
    {onRead && (
      <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #e8e8e8' }}>
        <Box
          onClick={!read ? onRead : undefined}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            cursor: read ? 'default' : 'pointer',
            userSelect: 'none',
            p: 1, borderRadius: 1.5,
            '&:hover': !read ? { background: 'rgba(46,125,50,0.06)' } : {},
          }}>
          <Checkbox
            checked={!!read}
            onChange={!read ? onRead : undefined}
            size="small"
            sx={{
              p: 0,
              color: '#bdbdbd',
              '&.Mui-checked': { color: '#2e7d32' },
            }}
          />
          <Typography variant="body2" sx={{
            color: read ? '#2e7d32' : '#444',
            fontWeight: read ? 700 : 400,
            fontSize: '0.85rem',
          }}>
            {read
              ? '✓ He leído y acepto las condiciones anteriores'
              : 'He leído y acepto las condiciones anteriores'}
          </Typography>
        </Box>
      </Box>
    )}
  </Paper>
);

const DateField = ({ label, value, onChange, required, error, helperText, maxYear, minYear }) => (
  <TextField fullWidth label={label} required={required} size="small" type="date" value={value}
    onChange={e => onChange(e.target.value)} error={error} helperText={helperText}
    InputLabelProps={{ shrink: true }}
    inputProps={{ max: `${maxYear || new Date().getFullYear()}-12-31`, min: `${minYear || 1920}-01-01` }} />
);

const STEPS = [
  { label: 'Partes del Contrato' },
  { label: 'Datos Personales' },
  { label: 'Documentos' },
  { label: 'Beneficiarios' },
  { label: 'Aportes' },
  { label: 'Obligaciones y Términos' },
  { label: 'Confidencialidad' },
  { label: 'Firma y Consentimiento' },
];

const INIT = {
  cooperativaId: '', nombre: '', apellidos: '', cedula: '', cedulaExpedida: '',
  fechaNacimiento: '', genero: 'M', estadoCivil: '',
  paisId: 1045, departamentoId: '', municipioId: '',
  direccion: '', codigoPostal: '', celular: '', celular2: '', email: '', codigoAlfaNum: '',
  beneficiarios: [
    { nombres: '', apellidos: '', cedula: '', contacto: '' },
    { nombres: '', apellidos: '', cedula: '', contacto: '' },
    { nombres: '', apellidos: '', cedula: '', contacto: '' },
  ],
  modalidadAporte: 'DINERO', fechaPrimeraCuota: '', valorBienes: '',
  readClauses: {},
  aceptaTerminos: false, declaracionVeracidad: false, autorizaDatos: false,
};

export default function ContratoAfiliacion({ open, onClose, onSuccess, cooperativaIdFijo = null }) {
  const [step, setStep] = useState(0);
  const [docIdentidad,    setDocIdentidad]    = useState(null);
  const [urlDocIdentidad, setUrlDocIdentidad] = useState(null);
  // Snapshot de los campos críticos al momento de subir/validar la cédula.
  // Si estos cambian después, la cédula queda invalidada (anti-suplantación).
  const [snapshotDatosCedula, setSnapshotDatosCedula] = useState(null);
  const [cedulaInvalidadaPorCambio, setCedulaInvalidadaPorCambio] = useState(false);
  const [docsPaso,        setDocsPaso]        = useState(0);
  const [docsAdicionales, setDocsAdicionales] = useState({
    rut:                     { file: null, url: null },
    antecedentes_procuraduria:{ file: null, url: null },
    antecedentes_contraloria: { file: null, url: null },
    antecedentes_policia:     { file: null, url: null },
    bancario:                 { file: null, url: null },
  });

  const scrollToDoc = (idx) => {
    setTimeout(() => {
      const el = document.getElementById(`doc-step-${idx}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const setDoc = (tipo, file, url) =>
    setDocsAdicionales(p => ({ ...p, [tipo]: { ...p[tipo], file, url } }));

  // Documentos marcados explícitamente como pendientes por el usuario
  const [docsPendientes, setDocsPendientes] = useState({
    rut: false, antecedentes_procuraduria: false,
    antecedentes_contraloria: false, antecedentes_policia: false, bancario: false,
  });
  const marcarPendiente = (tipo) =>
    setDocsPendientes(p => ({ ...p, [tipo]: true }));
  const desmarcarPendiente = (tipo) =>
    setDocsPendientes(p => ({ ...p, [tipo]: false }));

  // Un doc adicional está "resuelto" si fue subido O fue marcado como pendiente
  const docResuelto = (tipo) => !!docsAdicionales[tipo]?.url || docsPendientes[tipo];
  const todosDocsResueltos = ['rut','antecedentes_procuraduria','antecedentes_contraloria','antecedentes_policia','bancario']
    .every(t => docResuelto(t));
  const [form, setForm] = useState(INIT);
  const [cooperativas, setCooperativas] = useState([]);
  const [coopSeleccionada, setCoopSeleccionada] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [afiliadoId, setAfiliadoId] = useState(null);
  const [hashContrato, setHashContrato] = useState(null);
  const [pdfCargando, setPdfCargando]   = useState(false);
  const [pdfProgress, setPdfProgress]   = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [tsToken, setTsToken] = useState('');  const contentRef = useRef(null);

  const [paises, setPaises] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [loadingGeo, setLoadingGeo] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError,   setGeoError]   = useState('');
  const [emailStatus,       setEmailStatus]       = useState('idle'); // idle | checking | ok | taken | existente
  const [cuentasExistentes, setCuentasExistentes] = useState([]); // {Nombre, Email, CooperativaID}
  const [cuentaElegida,     setCuentaElegida]     = useState(null); // cuenta seleccionada por el usuario
  const [cedulaStatus, setCedulaStatus] = useState('idle'); // idle | checking | ok | taken

  useEffect(() => {
    if (open) {
      getPaises().then(d => setPaises(d || [])).catch(() => {});
      getCooperativasPublicas().then(d => {
        setCooperativas(d || []);
        if (cooperativaIdFijo) {
          const c = (d || []).find(x => x.cooperativaID === cooperativaIdFijo || x.cooperativaID === Number(cooperativaIdFijo));
          if (c) setCoopSeleccionada(c);
        }
      }).catch(() => {});
      // Si hay cooperativa fija (embebido en sitio de cooperativa), preseleccionar
      if (cooperativaIdFijo) {
        setForm(f => ({ ...f, cooperativaId: cooperativaIdFijo }));
      }
    }
  }, [open, cooperativaIdFijo]);

  useEffect(() => {
    if (!form.paisId) { setDepartamentos([]); return; }
    setLoadingGeo(true);
    getDepartamentos(form.paisId).then(d => setDepartamentos(d || [])).catch(() => setDepartamentos([])).finally(() => setLoadingGeo(false));
  }, [form.paisId]);

  useEffect(() => {
    if (!form.departamentoId) { setMunicipios([]); return; }
    setLoadingGeo(true);
    getMunicipios(form.departamentoId).then(d => setMunicipios(d || [])).catch(() => setMunicipios([])).finally(() => setLoadingGeo(false));
  }, [form.departamentoId]);

  // Verificar email disponible (debounce 700ms)
  // NOTA: no llamar setState síncronamente aquí — causaría re-render en cada tecla
  useEffect(() => {
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setEmailStatus('idle'); return;
    }
    const t = setTimeout(() => {
      setEmailStatus('checking');
      verificarEmail(form.email, form.cooperativaId || undefined)
        .then(d => setEmailStatus(d.disponible ? 'ok' : 'taken'))
        .catch(() => setEmailStatus('idle'));
    }, 800);
    return () => clearTimeout(t);
  }, [form.email, form.cooperativaId]);



  // Verificar cédula disponible (debounce 700ms)
  useEffect(() => {
    if (!form.cedula || form.cedula.trim().length < 5) {
      setCedulaStatus('idle'); return;
    }
    const t = setTimeout(() => {
      setCedulaStatus('checking');
      setCuentasExistentes([]);
      setCuentaElegida(null);
      verificarDocumento(form.cedula.trim(), form.cooperativaId || undefined)
        .then(d => {
          if (d.excluido) {
            setCedulaStatus('excluido');
          } else if (!d.disponible) {
            setCedulaStatus('taken');
          } else {
            setCedulaStatus('ok');
            // Tiene cuenta en otras cooperativas - mostrar selector
            if (d.cuentasExistentes?.length > 0)
              setCuentasExistentes(d.cuentasExistentes);
          }
        })
        .catch(() => setCedulaStatus('idle'));
    }, 700);
    return () => clearTimeout(t);
  }, [form.cedula, form.cooperativaId]);

  // Resetear cuenta elegida si cambia la cédula
  useEffect(() => {
    setCuentasExistentes([]);
    setCuentaElegida(null);
  }, [form.cedula]);

  useEffect(() => { if (contentRef.current) contentRef.current.scrollTo({ top: 0, behavior: 'smooth' }); }, [step]);

  // ── Anti-suplantación ────────────────────────────────────────────────
  // Si el usuario ya validó su cédula y ahora modificó alguno de los datos
  // personales críticos (nombre, apellidos, número o lugar de expedición),
  // invalidamos la cédula subida. Al volver al paso Documentos verá el
  // aviso y tendrá que re-subir.
  useEffect(() => {
    if (!urlDocIdentidad || !snapshotDatosCedula) return;
    const cambio =
      (form.nombre || '').trim()         !== snapshotDatosCedula.nombre ||
      (form.apellidos || '').trim()      !== snapshotDatosCedula.apellidos ||
      (form.cedula || '').trim()         !== snapshotDatosCedula.cedula ||
      (form.cedulaExpedida || '').trim() !== snapshotDatosCedula.cedulaExpedida;
    if (cambio) {
      setUrlDocIdentidad(null);
      setDocIdentidad(null);
      setCedulaInvalidadaPorCambio(true);
      // Reposicionar el sub-stepper al primer documento (cédula) para que
      // no quede en un adicional apuntando a un paso que ya no cumple.
      setDocsPaso(0);
    }
  }, [form.nombre, form.apellidos, form.cedula, form.cedulaExpedida, urlDocIdentidad, snapshotDatosCedula]);

  const sf = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const rc = (k) => setForm(p => ({ ...p, readClauses: { ...p.readClauses, [k]: new Date().toISOString() } }));
  const sb = (i, f, v) => { const u = [...form.beneficiarios]; u[i] = { ...u[i], [f]: v }; setForm(p => ({ ...p, beneficiarios: u })); };

  const handleGeo = useCallback(async () => {
    if (!form.paisId || !form.departamentoId || !form.municipioId) {
      setGeoError('Seleccione País, Departamento y Ciudad/Municipio antes de obtener el código postal.');
      return;
    }
    if (!form.direccion.trim()) {
      setGeoError('Ingrese la dirección para obtener el código postal.');
      return;
    }
    setGeoError('');
    setGeoLoading(true);
    // Comparar como strings para evitar fallos por tipo (número vs string)
    const pid = String(form.paisId);
    const did = String(form.departamentoId);
    const mid = String(form.municipioId);
    const pais   = paises.find(p  => String(p.PaisID  || p.paisID)  === pid)?.Nombre || paises.find(p => String(p.PaisID || p.paisID) === pid)?.nombre || '';
    const depto  = departamentos.find(d => String(d.DepartamentoID || d.departamentoID) === did)?.Nombre || departamentos.find(d => String(d.DepartamentoID || d.departamentoID) === did)?.nombre || '';
    const ciudad = municipios.find(m => String(m.MunicipioID || m.municipioID) === mid)?.Nombre   || municipios.find(m => String(m.MunicipioID || m.municipioID) === mid)?.nombre || '';
    console.log('[Geocoding] ubicación:', { pais, depto, ciudad });
    const geoResult = await geocodificarDireccion(form.direccion.trim(), ciudad, depto, pais);
    console.log('[Geocoding] resultado:', geoResult);
    if (geoResult.codigoPostal) {
      sf('codigoPostal', geoResult.codigoPostal);
      setGeoError('');
    } else {
      setGeoError('No se encontró código postal automáticamente. Puede ingresarlo manualmente.');
    }
    setGeoLoading(false);
  }, [form.direccion, form.paisId, form.departamentoId, form.municipioId, municipios, departamentos, paises]);

  const validate = () => {
    const e = {};
    if (step === 0 && !form.readClauses['intro']) e.rc = 'Debe leer y aceptar la cláusula para continuar.';
    if (step === 1) {
      if (!form.nombre.trim())    e.nombre    = 'Requerido';
      if (!form.apellidos.trim()) e.apellidos = 'Requerido';
      if (!form.cedula.trim())    e.cedula    = 'Requerido';
      else if (cedulaStatus === 'taken')    e.cedula = 'Esta cédula ya tiene una solicitud activa en esta cooperativa.';
      else if (cedulaStatus === 'checking') e.cedula = 'Verificando cédula…';
      else if (cedulaStatus === 'excluido') e.cedula = 'En este momento no es posible completar su solicitud de afiliación. Para más información contáctenos directamente.';
      if (!form.celular.trim())   e.celular   = 'Requerido';
      if (!form.email.trim())     e.email     = 'Requerido';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Email inválido';
      else if (emailStatus === 'taken') e.email = 'Este correo ya está registrado en esta cooperativa.';
      else if (emailStatus === 'checking') e.email = 'Verificando disponibilidad del correo…';
      if (!form.estadoCivil)      e.estadoCivil = 'Requerido';
      if (!form.fechaNacimiento)  e.fechaNac  = 'Requerido';
    }
    if (step === 2) {
      if (!docIdentidad)         e.rc = 'Debe subir su Cédula de Ciudadanía para continuar.';
      else if (!urlDocIdentidad) e.rc = 'La cédula debe pasar la verificación automática (OCR) antes de continuar.';
      else if (!todosDocsResueltos) e.rc = 'Debe subir o marcar como pendiente cada uno de los documentos adicionales.';
    }
    if (step === 5 && Object.keys(form.readClauses).filter(k => k.startsWith('ob')).length < 3) e.rc = 'Debe leer y aceptar todas las cláusulas.';
    if (step === 6 && !form.readClauses['conf']) e.rc = 'Debe aceptar la cláusula de confidencialidad.';
    if (step === 7) {
      if (!form.aceptaTerminos)       e.at = 'Requerido';
      if (!form.declaracionVeracidad) e.dv = 'Requerido';
      if (!form.autorizaDatos)        e.ad = 'Requerido';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Calcula en tiempo real si el step actual tiene sus condiciones mínimas cumplidas
  const stepListo = useMemo(() => {
    switch (step) {
      case 0: return !!form.readClauses['intro'];
      case 1: return (
        !!form.cooperativaId &&
        !!form.nombre.trim() &&
        !!form.apellidos.trim() &&
        !!form.cedula.trim() &&
        cedulaStatus === 'ok' &&
        !!form.celular.trim() &&
        !!form.email.trim() &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
        emailStatus === 'ok' &&
        (cuentasExistentes.length === 0 || cuentaElegida !== null) &&
        !!form.estadoCivil &&
        !!form.fechaNacimiento
      );
      case 2: return !!urlDocIdentidad && todosDocsResueltos;
      case 3: return !!form.readClauses['incorp']; // beneficiarios son opcionales
      case 4: return !!form.readClauses['obj5'];
      case 5: return ['ob7','ob8','ob9'].every(k => !!form.readClauses[k]);
      case 6: return !!form.readClauses['conf'];
      case 7: return !!form.aceptaTerminos && !!form.declaracionVeracidad && !!form.autorizaDatos && !!tsToken;
      default: return true;
    }
  }, [step, form, cedulaStatus, emailStatus, urlDocIdentidad, todosDocsResueltos, tsToken]);

  const handleNext = () => { if (validate()) { if (step < 7) setStep(s => s + 1); else handleSubmit(); } };
  const handleBack = () => {
    setErrors({});
    setStep(s => {
      // Anti-suplantación: al salir del paso Documentos (2) con ANTERIOR,
      // invalidamos la cédula sin importar si el usuario cambia o no datos
      // luego. Al volver debe re-subirla obligatoriamente. Se complementa
      // con el useEffect que también invalida si detecta cambios en los
      // datos personales críticos.
      if (s === 2) {
        setUrlDocIdentidad(null);
        setDocIdentidad(null);
        setSnapshotDatosCedula(null);
        setCedulaInvalidadaPorCambio(true);
        setDocsPaso(0);
      }
      return Math.max(0, s - 1);
    });
  };

  const handleSubmit = async () => {
    if (!tsToken) {
      setSubmitError('Espere a que se complete la verificación de seguridad, o refresque la página.');
      return;
    }
    setSubmitting(true); setSubmitError('');
    try {
      const respuesta = await registrarAfiliacion({
        nombres: form.nombre.trim(), apellidos: form.apellidos.trim(),
        tipoDocumento: 'CC', documento: form.cedula.trim(),
        documentoExpedidoEn: form.cedulaExpedida.trim() || null,
        fechaNacimiento: form.fechaNacimiento || null,
        genero: form.genero, estadoCivil: form.estadoCivil?.toUpperCase() || null,
        codigoAlfaNumerico: form.codigoAlfaNum.trim() || null,
        email: form.email.trim(), celular: form.celular.trim(), celular2: form.celular2.trim() || null,
        paisId: form.paisId || null, departamentoId: form.departamentoId || null, municipioId: form.municipioId || null,
        direccion: form.direccion.trim() || null, codigoPostal: form.codigoPostal.trim() || null,
        modalidadAporte: form.modalidadAporte, fechaPrimeraCuota: form.fechaPrimeraCuota || null,
        beneficiarios: form.beneficiarios.filter(b => b.nombres.trim() && b.apellidos.trim()).map(b => ({
          nombres: b.nombres.trim(), apellidos: b.apellidos.trim(), documento: b.cedula.trim() || null, contacto: b.contacto.trim() || null,
        })),
        urlDocIdentidad: urlDocIdentidad || null,
        cooperativaId: form.cooperativaId ? Number(form.cooperativaId) : 1,
        // Registro de aceptaciones de cláusulas con timestamps
        aceptacionesClausulas: Object.entries(form.readClauses)
          .map(([clausula, timestamp]) => ({ clausula, timestamp })),
        // Documentos adicionales subidos durante el registro
        urlDocRut:                     docsAdicionales.rut?.url || null,
        urlDocAntecedentesProcu:       docsAdicionales.antecedentes_procuraduria?.url || null,
        urlDocAntecedentesContraloria: docsAdicionales.antecedentes_contraloria?.url || null,
        urlDocAntecedentesPolicia:     docsAdicionales.antecedentes_policia?.url || null,
        urlDocBancario:                docsAdicionales.bancario?.url || null,
        // Documentos marcados como pendientes por el usuario
        docsPendientes: Object.entries(docsPendientes)
          .filter(([, v]) => v).map(([k]) => k),
        // Token Cloudflare Turnstile (anti-bot)
        turnstileToken: tsToken,
      });
      const afiliadoIdResp = respuesta?.afiliadoID || null;
      setAfiliadoId(afiliadoIdResp);
      setHashContrato(respuesta?.hashContrato || null);
      setSubmitted(true);
      // Notificar al padre para habilitar los pasos siguientes
      onSuccess?.({
        cedula:     form.cedula.trim(),
        nombres:    form.nombre.trim(),
        apellidos:  form.apellidos.trim(),
        estado:     'PENDIENTE',
        afiliadoId: afiliadoIdResp,
      });
    } catch (err) {
      setSubmitError(err?.data?.mensaje || err.message || 'Error al enviar. Intente nuevamente.');
    } finally { setSubmitting(false); }
  };

  const entidadPDF = coopSeleccionada ? {
    nombre:         coopSeleccionada.nombre             || 'FENAGRO APL',
    siglas:         coopSeleccionada.siglas              || '',
    tipoEntidad:    coopSeleccionada.tipoEntidad         || 'COOPERATIVA',
    nit:            coopSeleccionada.nit                 || '',
    representante:  coopSeleccionada.representanteLegal  || '',
    cedulaRep:      coopSeleccionada.cedulaRepresentante || '',
    direccion:      coopSeleccionada.direccion           || '',
    telefono:       coopSeleccionada.telefono            || '',
    email:          coopSeleccionada.email               || '',
    sitioWeb:       coopSeleccionada.sitioWeb            || '',
    cuentaBancaria: coopSeleccionada.cuentaBancaria      || '',
  } : null;

  // STEP 0
  const S0 = () => (
    <Box>
      <Typography variant="h6" sx={{ color: '#1a3a1a', fontWeight: 700, mb: 0.5 }}>Contrato de Afiliación y Aportes</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>Lea cuidadosamente antes de avanzar.</Typography>
      <ReadSection title={{ number: 'OBJETO', title: 'Contrato Asociativo de Emprendimiento Solidario' }} onRead={() => rc('intro')} read={!!form.readClauses['intro']}>
        <SectionText>El presente Contrato regula la relación de cooperación Socio-empresarial entre las redes del sector solidario cooperativo a través de la <strong>"CONFEDERACIÓN ALIANZA SOLIDARIA"</strong> y la Federación <strong>"FENAGRO APL"</strong>.</SectionText>
        <SectionText>En segunda instancia regula la relación entre la <strong>FUNDACIÓN ALIANZA SOCIAL ORG</strong> y la organización <strong>"ALIANZA SOLIDARIA DE CAPELLANES" / SOLIDARITY ALLIANCE OF CHAPLAINS</strong>.</SectionText>
        <SectionText>El Acuerdo incluye las <strong>POLÍTICAS DE PRODUCCIÓN LIMPIA (P+L)</strong> y los <strong>ACUERDOS DE PRODUCCIÓN LIMPIA (AP+L)</strong>. Marco legal: Ley 79/1988, Ley 454/1998, Ley 115/1994, Ley 1475/2014, Ley 133/1994, Ley 1780/2016, Ley 1834/2017, Ley 2069/2020, Ley 2535/2025.</SectionText>
      </ReadSection>
      {errors.rc && <Alert severity="warning">{errors.rc}</Alert>}
    </Box>
  );

  // STEP 1
  const S1 = () => (
    <Box>
      <Typography variant="h6" sx={{ color: '#1a3a1a', fontWeight: 700, mb: 0.5 }}>Datos del Asociado</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>Complete la información personal del Asociado Emprendedor Solidario.</Typography>
      <ClausulaTitle number="CLÁUSULA 2ª" title="Formato de Afiliación" />
      <Grid container spacing={2}>
        {/* Selector de cooperativa */}
        <Grid item xs={12}>
          <FormControl fullWidth size="small" required error={!!errors.cooperativaId}
            disabled={!!cooperativaIdFijo}>
            <InputLabel>Cooperativa a la que se afilia *</InputLabel>
            <Select value={form.cooperativaId} label="Cooperativa a la que se afilia *"
              onChange={e => {
                      sf('cooperativaId', e.target.value);
                      const c = cooperativas.find(x => x.cooperativaID === e.target.value || x.cooperativaID === Number(e.target.value));
                      setCoopSeleccionada(c || null);
                    }}>
              {cooperativas.map(c => (
                <MenuItem key={c.cooperativaID} value={c.cooperativaID}>
                  {c.nombre} {c.siglas ? `(${c.siglas})` : ''}
                </MenuItem>
              ))}
            </Select>
            {errors.cooperativaId && <Typography variant="caption" color="error" sx={{ mt:0.5, ml:1.5 }}>{errors.cooperativaId}</Typography>}
            {cooperativaIdFijo && <Typography variant="caption" color="text.secondary" sx={{ mt:0.5, ml:1.5 }}>Cooperativa asignada para este sitio</Typography>}
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}><TextField fullWidth label="Nombre(s)" required size="small" value={form.nombre} onChange={e => sf('nombre', e.target.value)} error={!!errors.nombre} helperText={errors.nombre} /></Grid>
        <Grid item xs={12} sm={6}><TextField fullWidth label="Apellidos" required size="small" value={form.apellidos} onChange={e => sf('apellidos', e.target.value)} error={!!errors.apellidos} helperText={errors.apellidos} /></Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Cédula de Ciudadanía" required size="small"
            value={form.cedula}
            onChange={e => { sf('cedula', e.target.value); setCedulaStatus('idle'); }}
            error={!!errors.cedula || cedulaStatus === 'taken'}
            helperText={
              errors.cedula ? errors.cedula :
              cedulaStatus === 'checking' ? '⏳ Verificando…' :
              cedulaStatus === 'ok'       ? '✅ Cédula disponible' :
              cedulaStatus === 'taken'    ? '❌ Ya existe en esta cooperativa' : ' '
            }
            InputProps={{ endAdornment: cedulaStatus === 'checking'
              ? <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>
              : cedulaStatus === 'ok'
              ? <InputAdornment position="end"><CheckCircleOutlineIcon sx={{ color:'#2e7d32', fontSize:18 }} /></InputAdornment>
              : null
            }}
          />
        </Grid>
        {/* Selector de cuenta existente cuando la cédula ya tiene cuenta en otra cooperativa */}
        {cedulaStatus === 'ok' && cuentasExistentes.length > 0 && (
          <Grid item xs={12}>
            <Box sx={{ p:2, borderRadius:2, border:'2px solid #1565c0', background:'#e3f2fd' }}>
              <Typography variant="body2" sx={{ fontWeight:700, color:'#1565c0', mb:0.5 }}>
                ℹ️ Esta cédula ya tiene cuenta en el sistema
              </Typography>
              <Typography variant="body2" sx={{ color:'#555', mb:1.5 }}>
                Está afiliado en las siguientes cooperativas. ¿Desea usar alguna de estas cuentas o registrar una nueva?
              </Typography>
              <Box sx={{ display:'flex', flexDirection:'column', gap:1, mb:1.5 }}>
                {cuentasExistentes.map((c, i) => (
                  <Button key={i} variant={cuentaElegida?.Email === c.email ? 'contained' : 'outlined'}
                    onClick={() => { setCuentaElegida(c); sf('email', c.email); setEmailStatus('ok'); }}
                    sx={{ justifyContent:'space-between', borderRadius:2,
                      borderColor: cuentaElegida?.Email === c.email ? '#1565c0' : '#90caf9',
                      background: cuentaElegida?.Email === c.email ? '#1565c0' : 'transparent',
                      color: cuentaElegida?.Email === c.email ? '#fff' : '#1565c0' }}>
                    <span>{c.email}</span>
                    <Chip label={c.nombre} size="small"
                      sx={{ fontSize:'0.65rem', background:'rgba(255,255,255,0.3)', color:'inherit' }}/>
                  </Button>
                ))}
              </Box>
              <Button size="small" variant="text"
                sx={{ color:'#555', fontSize:'0.78rem' }}
                onClick={() => { setCuentaElegida({ nueva: true }); sf('email', ''); setEmailStatus('idle'); }}>
                + Registrar con un correo diferente
              </Button>
              {cuentaElegida?.nueva && (
                <Alert severity="info" sx={{ mt:1, fontSize:'0.78rem' }}>
                  Ingrese un correo nuevo en el campo Email. Se creará una cuenta separada.
                </Alert>
              )}
            </Box>
          </Grid>
        )}        <Grid item xs={12} sm={6}><TextField fullWidth label="Expedida en (ciudad)" size="small" value={form.cedulaExpedida} onChange={e => sf('cedulaExpedida', e.target.value)} /></Grid>
        {/* ── Fecha de nacimiento · Género · Estado civil — misma fila ── */}
        <Grid item xs={12} sm={4}>
          <DateField label="Fecha de Nacimiento" value={form.fechaNacimiento}
            onChange={v => sf('fechaNacimiento', v)} required
            maxYear={new Date().getFullYear() - 18} error={!!errors.fechaNac}
            helperText={errors.fechaNac || ' '} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl size="small" fullWidth>
            <InputLabel>Género</InputLabel>
            <Select value={form.genero} label="Género" onChange={e => sf('genero', e.target.value)}>
              <MenuItem value="M">Masculino</MenuItem>
              <MenuItem value="F">Femenino</MenuItem>
              <MenuItem value="NB">No binario</MenuItem>
              <MenuItem value="O">Otro</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl size="small" fullWidth required error={!!errors.estadoCivil}>
            <InputLabel>Estado Civil *</InputLabel>
            <Select value={form.estadoCivil} label="Estado Civil *"
              onChange={e => sf('estadoCivil', e.target.value)}>
              <MenuItem value="SOLTERO">Soltero(a)</MenuItem>
              <MenuItem value="CASADO">Casado(a)</MenuItem>
              <MenuItem value="DIVORCIADO">Divorciado(a)</MenuItem>
              <MenuItem value="VIUDO">Viudo(a)</MenuItem>
              <MenuItem value="UNION_LIBRE">Unión libre</MenuItem>
              <MenuItem value="OTRO">Otro</MenuItem>
            </Select>
            {errors.estadoCivil && (
              <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.3, display: 'block' }}>
                {errors.estadoCivil}
              </Typography>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} sx={{ pt: '4px !important' }}><Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 700, lineHeight: 1 }}>Ubicación de residencia</Typography></Grid>
        <Grid item xs={12} sm={4}>
          <FormControl size="small" fullWidth>
            <InputLabel>País</InputLabel>
            <Select value={form.paisId} label="País" onChange={e => { sf('paisId', e.target.value); sf('departamentoId', ''); sf('municipioId', ''); }}>
              {(paises.length ? paises : [{ PaisID: 1045, Nombre: 'Colombia' }]).map(p => <MenuItem key={p.PaisID || p.paisID} value={p.PaisID || p.paisID}>{p.Nombre || p.nombre}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl size="small" fullWidth disabled={!form.paisId || loadingGeo}>
            <InputLabel>{loadingGeo ? 'Cargando…' : 'Departamento'}</InputLabel>
            <Select value={form.departamentoId} label="Departamento" onChange={e => { sf('departamentoId', e.target.value); sf('municipioId', ''); }}>
              <MenuItem value=""><em>Seleccione…</em></MenuItem>
              {departamentos.map(d => <MenuItem key={d.DepartamentoID || d.departamentoID} value={d.DepartamentoID || d.departamentoID}>{d.Nombre || d.nombre}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl size="small" fullWidth disabled={!form.departamentoId || loadingGeo}>
            <InputLabel>{loadingGeo ? 'Cargando…' : 'Ciudad / Municipio'}</InputLabel>
            <Select value={form.municipioId} label="Ciudad / Municipio" onChange={e => sf('municipioId', e.target.value)}>
              <MenuItem value=""><em>Seleccione…</em></MenuItem>
              {municipios.map(m => <MenuItem key={m.MunicipioID || m.municipioID} value={m.MunicipioID || m.municipioID}>{m.Nombre || m.nombre}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={8}>
          <TextField fullWidth label="Dirección" size="small" value={form.direccion} onChange={e => sf('direccion', e.target.value)}
            InputProps={{ endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Obtener código postal automáticamente con Google Maps">
                  <span>
                    <IconButton size="small" onClick={handleGeo} disabled={!form.direccion.trim() || geoLoading} color="success">
                      {geoLoading ? <CircularProgress size={16} /> : <MyLocationIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            )}} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth label="Código Postal" size="small"
            value={form.codigoPostal}
            onChange={e => { sf('codigoPostal', e.target.value); setGeoError(''); }}
            helperText={geoError || 'Use 📍 para obtenerlo automáticamente'}
            error={!!geoError && !form.codigoPostal}
            FormHelperTextProps={{ sx: { color: geoError ? (form.codigoPostal ? '#e65100' : '#c62828') : '#888' } }}
          />
        </Grid>
        <Grid item xs={12} sm={5}><TextField fullWidth label="Celular" required size="small" value={form.celular} onChange={e => sf('celular', e.target.value)} error={!!errors.celular} helperText={errors.celular} InputProps={{ startAdornment: <InputAdornment position="start">+57</InputAdornment> }} /></Grid>
        <Grid item xs={12} sm={5}><TextField fullWidth label="Celular 2 (opcional)" size="small" value={form.celular2} onChange={e => sf('celular2', e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start">+57</InputAdornment> }} /></Grid>
        <Grid item xs={12} sm={8}>
          <TextField fullWidth label="Email" required size="small" type="email" value={form.email}
            onChange={e => { sf('email', e.target.value); setEmailStatus('idle'); }}
            error={!!errors.email || emailStatus === 'taken'}
            helperText={
              errors.email ? errors.email :
              emailStatus === 'checking'  ? '⏳ Verificando disponibilidad…' :
              emailStatus === 'ok'        ? '✅ Correo disponible' :

              emailStatus === 'taken'     ? '❌ Este correo ya está registrado en esta cooperativa' : ' '
            }
            InputProps={{ endAdornment: emailStatus === 'checking'
              ? <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>
              : null }} />

        </Grid>
        <Grid item xs={12} sm={4}><TextField fullWidth label="Código Alfa Numérico" size="small" value={form.codigoAlfaNum} onChange={e => sf('codigoAlfaNum', e.target.value)} /></Grid>
      </Grid>
    </Box>
  );

  // STEP 2 — Documentos
  const DOCS_S2 = [
    { tipo: 'cedula',                   label: 'Cédula de Ciudadanía',        obligatorio: true,  icon: '🪪', desc: 'Documento vigente — anverso y reverso en una sola imagen o PDF.' },
    { tipo: 'rut',                      label: 'RUT actualizado',             obligatorio: false, icon: '📄', desc: 'Registro Único Tributario expedido por la DIAN con actividad económica relacionada con FENAGRO.' },
    { tipo: 'antecedentes_procuraduria',label: 'Antecedentes Procuraduría',   obligatorio: false, icon: '⚖️', desc: 'Certificado de la Procuraduría General de la Nación — vigencia máxima 30 días.' },
    { tipo: 'antecedentes_contraloria', label: 'Antecedentes Contraloría',    obligatorio: false, icon: '🏛️', desc: 'Certificado de la Contraloría General de la República — vigencia máxima 30 días.' },
    { tipo: 'antecedentes_policia',     label: 'Antecedentes Policía',        obligatorio: false, icon: '👮', desc: 'Certificado judicial de la Policía Nacional — vigencia máxima 30 días.' },
    { tipo: 'bancario',                 label: 'Certificado Bancario',        obligatorio: false, icon: '🏦', desc: 'Carta del banco certificando cuenta activa a su nombre para los aportes de la cooperativa.' },
  ];

  const S2 = () => (
    <Box>
      <Typography variant="h6" sx={{ color: '#1a3a1a', fontWeight: 700, mb: 0.5 }}>Documentos</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
        Suba sus documentos para que el revisor evalúe su solicitud. La cédula es obligatoria para continuar; los demás son opcionales pero necesarios para la aprobación.
      </Typography>

      <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
        📋 El revisor evaluará todos sus documentos antes de aprobar. Puede avanzar sin subir todos, pero su solicitud quedará como <strong>pendiente de documentos</strong>.
      </Alert>

      {cedulaInvalidadaPorCambio && (
        <Alert
          severity="warning"
          sx={{ mb: 1.5, fontSize: '0.85rem', fontWeight: 500 }}
          onClose={() => setCedulaInvalidadaPorCambio(false)}
        >
          🔒 <strong>Por seguridad, debes volver a subir tu Cédula de Ciudadanía.</strong>
          {' '}Cada vez que regresas a este paso o modificas tus datos personales
          se requiere una nueva validación. Este control impide suplantación
          con datos alterados después de la verificación.
        </Alert>
      )}

      {/* Indicador de progreso */}
      <Box sx={{ mb: 2.5, p:1.5, borderRadius:2, background: urlDocIdentidad && todosDocsResueltos ? '#e8f5e9' : '#fff8e1',
        border: `1px solid ${urlDocIdentidad && todosDocsResueltos ? '#a5d6a7' : '#ffe082'}`, display:'flex', alignItems:'center', gap:1 }}>
        {urlDocIdentidad && todosDocsResueltos
          ? <><CheckCircleOutlineIcon sx={{ color:'#2e7d32', fontSize:18 }}/><Typography variant="caption" sx={{ color:'#2e7d32', fontWeight:700 }}>✅ Todos los documentos listos — puede continuar</Typography></>
          : <>
              <Typography variant="caption" sx={{ color:'#e65100', fontWeight:600 }}>
                {!urlDocIdentidad
                  ? '❌ Cédula pendiente de verificación OCR'
                  : `⚠️ Faltan ${['rut','antecedentes_procuraduria','antecedentes_contraloria','antecedentes_policia','bancario'].filter(t => !docResuelto(t)).length} documento(s) por subir o marcar como pendiente`}
              </Typography>
            </>
        }
      </Box>

      <Stepper activeStep={docsPaso} orientation="vertical">
        {DOCS_S2.map((doc, idx) => {
          const urlDoc = idx === 0 ? urlDocIdentidad : docsAdicionales[doc.tipo]?.url;
          const valido  = idx === 0 ? !!urlDocIdentidad : !!docsAdicionales[doc.tipo]?.url;
          return (
            <Step key={doc.tipo} expanded={docsPaso === idx || valido}><Box id={`doc-step-${idx}`} sx={{ position:'relative', top:-80 }}/>
              <StepLabel
                StepIconComponent={() => (
                  <Box sx={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    background: valido ? '#2e7d32' : docsPaso === idx ? '#1a4d2e' : '#e8e8e8',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: docsPaso === idx ? '0 0 0 3px rgba(26,77,46,0.2)' : 'none',
                    fontSize: 18, transition: 'all 0.3s',
                  }}>
                    {valido
                      ? <CheckCircleOutlineIcon sx={{ fontSize: 20, color: '#fff' }}/>
                      : <span>{doc.icon}</span>}
                  </Box>
                )}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: docsPaso >= idx ? 700 : 400, color: docsPaso >= idx ? '#1a4d2e' : '#aaa', fontSize: '0.95rem' }}>
                    {doc.label}
                  </Typography>
                  {doc.obligatorio && <Chip label="Obligatorio" size="small" color="primary" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700 }}/>}
                  {valido && <Chip label="Verificado ✓" size="small" color="success" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700 }}/>}
                </Box>
              </StepLabel>
              <MuiStepContent>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>{doc.desc}</Typography>
                <VerificadorDocumento
                  titulo=""
                  descripcion="Formatos aceptados: PDF, JPG, PNG. Máximo 10MB."
                  cedula={form.cedula}
                  nombres={form.nombre}
                  apellidos={form.apellidos}
                  tipoDocumento={doc.tipo.startsWith('antecedentes') ? 'antecedentes' : doc.tipo}
                  onValido={(file, resultado) => {
                    if (idx === 0) {
                      setDocIdentidad(file);
                      setUrlDocIdentidad(resultado?.urlDocumento || null);
                      // Anti-suplantación: guardar snapshot de los datos personales
                      // al momento en que la cédula queda validada. Si el usuario
                      // vuelve al paso Datos Personales y modifica alguno de estos
                      // campos, la cédula se invalida y hay que re-subirla.
                      if (resultado?.urlDocumento) {
                        setSnapshotDatosCedula({
                          nombre:         (form.nombre || '').trim(),
                          apellidos:      (form.apellidos || '').trim(),
                          cedula:         (form.cedula || '').trim(),
                          cedulaExpedida: (form.cedulaExpedida || '').trim(),
                        });
                        setCedulaInvalidadaPorCambio(false);
                      }
                      if (resultado?.urlDocumento) setTimeout(() => { setDocsPaso(1); scrollToDoc(1); }, 800);
                    } else {
                      setDoc(doc.tipo, file, resultado?.urlDocumento || null);
                      if (resultado?.urlDocumento) setTimeout(() => { setDocsPaso(p => { const n = Math.min(5, p+1); scrollToDoc(n); return n; }); }, 800);
                    }
                  }}
                  onInvalido={() => {
                    if (idx === 0) setDocIdentidad(null);
                    else setDoc(doc.tipo, null, null);
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  {idx > 0 && (
                    <Button size="small" variant="text" onClick={() => setDocsPaso(p => Math.max(0, p - 1))}
                      sx={{ color: '#888', fontSize: '0.75rem' }}>← Anterior</Button>
                  )}
                  {/* Cédula verificada: mostrar siguiente */}
                  {idx === 0 && valido && (
                    <Button size="small" variant="outlined" onClick={() => { setDocsPaso(1); scrollToDoc(1); }}
                      sx={{ borderColor: '#1a4d2e', color: '#1a4d2e', fontSize: '0.75rem', borderRadius: 4 }}>
                      Siguiente →
                    </Button>
                  )}
                  {/* Docs opcionales verificados: mostrar siguiente */}
                  {idx > 0 && valido && idx < 5 && (
                    <Button size="small" variant="outlined" onClick={() => { setDocsPaso(p => { const n = Math.min(5, p+1); scrollToDoc(n); return n; }); }}
                      sx={{ borderColor: '#1a4d2e', color: '#1a4d2e', fontSize: '0.75rem', borderRadius: 4 }}>
                      Siguiente →
                    </Button>
                  )}
                  {/* Botón marcar pendiente — solo para docs opcionales (idx > 0) sin archivo subido */}
                  {idx > 0 && !valido && !docsPendientes[doc.tipo] && (
                    <Button size="small" variant="outlined" color="warning"
                      onClick={() => {
                        if (window.confirm(
                          `⚠️ ADVERTENCIA\n\nSi marca "${doc.label}" como pendiente, su solicitud quedará con documentos incompletos.\n\nEl revisor puede RECHAZAR su afiliación por falta de este documento.\n\n¿Confirma marcar como pendiente?`
                        )) {
                          marcarPendiente(doc.tipo);
                          if (idx < 5) setTimeout(() => { setDocsPaso(p => { const n = Math.min(5, p+1); scrollToDoc(n); return n; }); }, 300);
                        }
                      }}
                      sx={{ fontSize: '0.72rem', borderRadius: 4, borderColor: '#ed6c02', color: '#ed6c02' }}>
                      ⚠️ Marcar como pendiente
                    </Button>
                  )}
                  {/* Indicador de pendiente con opción de desmarcar */}
                  {idx > 0 && docsPendientes[doc.tipo] && !valido && (
                    <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                      <Chip label="⚠️ Pendiente de entrega" size="small"
                        sx={{ background:'#fff3e0', color:'#e65100', fontWeight:700, fontSize:'0.68rem', border:'1px solid #ffcc02' }}/>
                      <Button size="small" variant="text"
                        onClick={() => desmarcarPendiente(doc.tipo)}
                        sx={{ color:'#888', fontSize:'0.68rem', textDecoration:'underline', minWidth:'auto' }}>
                        Subir ahora
                      </Button>
                      {idx < 5 && (
                        <Button size="small" variant="outlined" onClick={() => { setDocsPaso(p => { const n = Math.min(5, p+1); scrollToDoc(n); return n; }); }}
                          sx={{ borderColor: '#1a4d2e', color: '#1a4d2e', fontSize: '0.72rem', borderRadius: 4 }}>
                          Siguiente →
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              </MuiStepContent>
            </Step>
          );
        })}
      </Stepper>

      <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, background: '#f5f5f5', display: 'flex', gap: 1, alignItems: 'center' }}>
        <LockIcon sx={{ fontSize: 16, color: '#888' }}/>
        <Typography variant="caption" sx={{ color: '#666' }}>
          Sus documentos se usan únicamente para verificar su identidad e idoneidad. Solo el revisor asignado los podrá ver.
        </Typography>
      </Box>
    </Box>
  );

  // STEP 3
  const S3 = () => (
    <Box>
      <Typography variant="h6" sx={{ color: '#1a3a1a', fontWeight: 700, mb: 0.5 }}>Declaración de Beneficiarios</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>En caso de fallecimiento, estas personas dispondrán del saldo en partes iguales.</Typography>
      <ReadSection title={{ number: 'CLÁUSULA 3ª', title: 'Contrato de Incorporación y Cooperación Empresarial' }} onRead={() => rc('incorp')} read={!!form.readClauses['incorp']}>
        <SectionText>El afiliado designa como beneficiario(s) a la(s) persona(s) que podrán solicitar la devolución de sus aportes, quienes recibirán el dinero de acuerdo a lo estipulado en el acto asociativo.</SectionText>
        <SectionText><strong>Declaración:</strong> Los datos proporcionados son reales. La decisión de afiliarse es de entera voluntad a los programas solidarios <em>"CONSUMO COLABORATIVO O BANCO DE TIEMPO"</em>.</SectionText>
      </ReadSection>
      <ClausulaTitle number="CLÁUSULA 4ª" title="Beneficiarios de la Cuenta de Emprendimiento" />
      <Typography variant="caption" sx={{ color: '#666', mb: 2, display: 'block' }}>Puede dejar en blanco los que no apliquen.</Typography>
      {form.beneficiarios.map((b, i) => (
        <Paper key={i} elevation={0} sx={{ border: '1px solid #e8f5e9', borderRadius: 2, p: 2, mb: 2, background: '#f9fbe7' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32', mb: 1.5, display: 'block' }}>Beneficiario {i + 1}</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Nombres" size="small" value={b.nombres} onChange={e => sb(i, 'nombres', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Apellidos" size="small" value={b.apellidos} onChange={e => sb(i, 'apellidos', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Cédula" size="small" value={b.cedula} onChange={e => sb(i, 'cedula', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Celular o Email" size="small" value={b.contacto} onChange={e => sb(i, 'contacto', e.target.value)} /></Grid>
          </Grid>
        </Paper>
      ))}
    </Box>
  );

  // STEP 4
  const S4 = () => (
    <Box>
      <Typography variant="h6" sx={{ color: '#1a3a1a', fontWeight: 700, mb: 0.5 }}>Formato de Aporte</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>Defina su modalidad de aporte y las condiciones económicas de su afiliación.</Typography>
      <ReadSection title={{ number: 'CLÁUSULA 5ª', title: 'Objeto de la Inscripción y Aporte' }} onRead={() => rc('obj5')} read={!!form.readClauses['obj5']}>
        <SectionText>El Asociado se obliga a entregar a la Confederación la cantidad estipulada, con el objeto de acreditar el capital pago exigido por la Superintendencia de Economía Solidaria, de conformidad al <strong>artículo 48 de la Ley 79 de 1988</strong>.</SectionText>
      </ReadSection>
      <ClausulaTitle number="CLÁUSULA 6ª" title="Formato de Aporte" />
      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 2, background: '#fafafa' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#444' }}>Afiliación a la cooperativa</Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>Pago único — cuota de inscripción cooperativa</Typography>
          </Box>
          <Chip label="$75.000 COP" color="success" size="small" sx={{ fontWeight: 700 }} />
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#444' }}>Afiliación red de Consumo Colaborativo</Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>Plataforma digital de pro$umo colaborativo</Typography>
          </Box>
          <Chip label="$75.000 COP" color="success" size="small" sx={{ fontWeight: 700 }} />
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#444' }}>Aporte mínimo mensual</Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>Consumo colaborativo en establecimientos autorizados</Typography>
          </Box>
          <Chip label="$400.000 COP/mes" color="warning" variant="outlined" size="small" sx={{ fontWeight: 700 }} />
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <FormLabel component="legend" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#2e7d32', mb: 1 }}>Modalidad de pago del aporte mensual:</FormLabel>
          <RadioGroup value={form.modalidadAporte} onChange={e => sf('modalidadAporte', e.target.value)}>
            <FormControlLabel value="TRABAJO" control={<Radio size="small" color="success" />} label={<Typography variant="body2"><strong>Trabajo y Servicios</strong> — Mín. 2 h, máx. 6 h/día</Typography>} />
            <FormControlLabel value="BIENES"  control={<Radio size="small" color="success" />} label={<Typography variant="body2"><strong>Bienes</strong> — En especie (valor equivalente)</Typography>} />
            <FormControlLabel value="DINERO"  control={<Radio size="small" color="success" />} label={<Typography variant="body2"><strong>Dinero</strong> — $400.000 COP/mes</Typography>} />
          </RadioGroup>
        </FormControl>
        {form.modalidadAporte === 'BIENES' && (
          <TextField fullWidth label="Valor en bienes ($)" size="small" type="number" value={form.valorBienes} onChange={e => sf('valorBienes', e.target.value)} sx={{ mt: 2 }} />
        )}
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#444', display: 'block', mb: 1.5 }}>Fecha estimada de primera cuota:</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <DateField label="Fecha primera cuota" value={form.fechaPrimeraCuota} onChange={v => sf('fechaPrimeraCuota', v)} minYear={new Date().getFullYear()} maxYear={new Date().getFullYear() + 2} />
          </Grid>
        </Grid>
        <Alert severity="info" icon={false} sx={{ mt: 2, fontSize: '0.82rem', background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 2 }}>
          💳 Los aportes en dinero deben realizarse a través del <strong>botón de pagos de este sitio web</strong>.
          El enlace de pago estará disponible en su portal de afiliado una vez aprobada su solicitud.
        </Alert>
      </Paper>
    </Box>
  );

  // STEP 5
  const S5 = () => (
    <Box>
      <Typography variant="h6" sx={{ color: '#1a3a1a', fontWeight: 700, mb: 0.5 }}>Obligaciones y Términos</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>Lea y acepte cada una de las siguientes cláusulas.</Typography>
      <ReadSection title={{ number: 'CLÁUSULA 7ª', title: 'Plazo o Término de Duración' }} onRead={() => rc('ob7')} read={!!form.readClauses['ob7']}>
        <SectionText>El presente convenio tiene como término de duración lo establecido en los programas de Emprendimiento de la confederación. Podrá darse por revocado por cualquier parte por escrito de mutuo acuerdo, o prorrogarse mediante contrato adicional escrito.</SectionText>
      </ReadSection>
      <ReadSection title={{ number: 'CLÁUSULA 8ª', title: 'Obligaciones de Incorporación' }} onRead={() => rc('ob8')} read={!!form.readClauses['ob8']}>
        <SectionText>Son obligaciones del Asociado: (a) Manifestar por escrito el deseo de pertenecer a la Red, pagar la afiliación y aportar documentos (cédula, RUT, antecedentes de Contraloría, Procuraduría, Policía y Certificado Bancario). (b) Inscribirse en la línea agropecuaria. (c) Aceptar estatutos y reglamentos. (d) Afiliarse como capellán y recibir el curso de capellanías. (e) Usar uniformes de gala o trabajo. (f) Afiliarse a la plataforma de consumo colaborativo.</SectionText>
      </ReadSection>
      <ReadSection title={{ number: 'CLÁUSULA 9ª', title: 'Obligaciones del Ente Jurídico Asociado' }} onRead={() => rc('ob9')} read={!!form.readClauses['ob9']}>
        <SectionText>La Fundación Alianza Social ORG y "ALIANZA SOLIDARIA DE CAPELLANES" se obligan a: (a) Reintegrar aportes a través del Fondo para Amortización. (b) Suministrar información pertinente. (c) Cubrir gastos de prestación de servicios. (d) Retribuir beneficios de consumo colaborativo (alimentos, turismo, salud, farmacia, gimnasio, mutual, funerario y más). (e) Suministrar compensaciones de establecimientos aliados.</SectionText>
      </ReadSection>
      {errors.rc && <Alert severity="warning">{errors.rc}</Alert>}
    </Box>
  );

  // STEP 6
  const S6 = () => (
    <Box>
      <Typography variant="h6" sx={{ color: '#1a3a1a', fontWeight: 700, mb: 0.5 }}>Propiedad Intelectual y Confidencialidad</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>Cláusulas sobre propiedad intelectual, derechos de autor y reserva.</Typography>
      <ReadSection title={{ number: 'CLÁUSULAS 10ª–11ª', title: 'Propiedad Intelectual' }} onRead={() => rc('prop')} read={!!form.readClauses['prop']}>
        <SectionText>Todo tipo de creación durante el contrato quedará en cabeza de las partes intervinientes. Cada parte se reserva la titularidad sobre sus derechos de propiedad intelectual preexistentes, incluyendo inventos, métodos, secretos empresariales, bases de datos, software, know how e información comercial.</SectionText>
      </ReadSection>
      <ReadSection title={{ number: 'CLÁUSULA 12ª', title: 'Confidencialidad y Obligación de Reserva' }} onRead={() => rc('conf')} read={!!form.readClauses['conf']}>
        <SectionText>Las partes se obligan a: (a) Guardar absoluta confidencialidad. (b) No editar, copiar ni reproducir información confidencial. (c) No utilizarla en actividades distintas a las acordadas. (d) Devolver toda la información confidencial al terminar el contrato. (e) Asumir responsabilidad por daños derivados del mal manejo. La información confidencial incluye secretos empresariales, bases de datos, reportes, estudios y toda información técnica y comercial.</SectionText>
      </ReadSection>
      {errors.rc && <Alert severity="warning">{errors.rc}</Alert>}
    </Box>
  );

  // STEP 7
  const S7 = () => (
    <Box>
      <Typography variant="h6" sx={{ color: '#1a3a1a', fontWeight: 700, mb: 0.5 }}>Firma y Consentimiento</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>Cláusula 14ª – Acuerdo Total · Cláusula 16ª – Consentimiento.</Typography>
      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 3, background: '#f9fbe7' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a3a1a', mb: 1.5 }}>Resumen de su afiliación:</Typography>
        <Grid container spacing={1}>
          {[['Nombre completo', `${form.nombre} ${form.apellidos}`], ['Cédula', form.cedula], ['Celular', form.celular], ['Email', form.email], ['Estado civil', form.estadoCivil], ['Modalidad de aporte', form.modalidadAporte]].map(([l, v]) => (
            <React.Fragment key={l}>
              <Grid item xs={5}><Typography variant="caption" sx={{ fontWeight: 600 }}>{l}:</Typography></Grid>
              <Grid item xs={7}><Typography variant="caption">{v || '—'}</Typography></Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Paper>
      {[
        { f: 'aceptaTerminos', ek: 'at', lbl: 'He leído, entendido y acepto los Términos y Condiciones del presente Contrato o Convenio Asociativo de Emprendimiento Solidario y Economías Colaborativas.' },
        { f: 'declaracionVeracidad', ek: 'dv', lbl: 'Declaro que toda la información proporcionada es correcta y corresponde a la verdad. Asumo plena responsabilidad por los datos suministrados.' },
        { f: 'autorizaDatos', ek: 'ad', lbl: 'Autorizo a la Confederación "ALIANZA SOLIDARIA" y a FENAGRO APL para el tratamiento de mis datos personales de conformidad con la Ley 1581 de 2012 y la Ley 594 de 2000.' },
      ].map(({ f, ek, lbl }) => (
        <Paper key={f} elevation={0} onClick={() => sf(f, !form[f])}
          sx={{ border: form[f] ? '1.5px solid #43a047' : '1px solid #e0e0e0', borderRadius: 2, p: 2, mb: 1.5, cursor: 'pointer', background: form[f] ? 'rgba(67,160,71,0.04)' : '#fff', transition: 'all 0.2s' }}>
          <FormControlLabel control={<Checkbox checked={form[f]} color="success" onChange={e => sf(f, e.target.checked)} onClick={e => e.stopPropagation()} />}
            label={<Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{lbl}</Typography>} />
          {errors[ek] && <Typography variant="caption" color="error" sx={{ ml: 4 }}>Requerido</Typography>}
        </Paper>
      ))}
      {/* Anti-bot Turnstile (invisible) */}
      <TurnstileWidget onToken={setTsToken} action="register" />
      {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
    </Box>
  );

  const steps = [S0, S1, S2, S3, S4, S5, S6, S7];

  // Resolver nombres de geo para el PDF
  const geoParaPDF = {
    pais: (paises.find(p => (p.PaisID||p.paisID) === form.paisId)?.Nombre) || 'Colombia',
    departamento: (departamentos.find(d => (d.DepartamentoID||d.departamentoID) === form.departamentoId)?.Nombre) || '',
    municipio: (municipios.find(m => (m.MunicipioID||m.municipioID) === form.municipioId)?.Nombre) || '',
  };

  if (submitted) return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      {/* Barra verde top */}
      <Box sx={{ height: 4, background: 'linear-gradient(90deg, #2e7d32, #66bb6a)' }} />
      <DialogContent sx={{ textAlign: 'center', py: 4, px: 4 }}>
        <Box sx={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg, #2e7d32, #43a047)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2.5 }}>
          <CheckCircleOutlineIcon sx={{ color: '#fff', fontSize: 42 }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a3a1a', mb: 1 }}>
          ¡Solicitud enviada!
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
          Su solicitud fue recibida exitosamente.
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          Nos comunicaremos a <strong>{form.email}</strong> para confirmar su cuenta.
        </Typography>
        <Chip label="Estado: PENDIENTE DE VALIDACIÓN" color="warning" sx={{ mb: 3, fontWeight: 700 }} />

        {/* Sección PDF */}
        <Box sx={{ background: '#f8fdf8', border: '1.5px solid #c8e6c9', borderRadius: 2, p: 2.5, mb: 2.5, textAlign: 'left' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a3a1a', mb: 0.8 }}>
            📄 Contrato de afiliación
          </Typography>
          <Typography variant="body2" sx={{ color: '#555', fontSize: '0.8rem', mb: 1.8 }}>
            Descargue su contrato, imprímalo, fírmelo físicamente y si lo requiere
            llévelo a notaría para autenticación de firmas. Luego escanéelo y envíelo
            para verificación a <strong>fondoemprendimientofenagro@gmail.com</strong>
          </Typography>
          <Button
            fullWidth variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => descargarContratoPDF(form, afiliadoId, geoParaPDF, hashContrato, entidadPDF)}
            sx={{ background:'linear-gradient(135deg,#1a4d2e,#2e7d32)', borderRadius:2, fontWeight:700, py:1.1, mb:1,
              '&:hover':{ background:'linear-gradient(135deg,#0e2a18,#1a4d2e)' } }}
          >
            Descargar contrato FENAGRO (PDF)
          </Button>
          <Button
            fullWidth variant="outlined" color="success"
            startIcon={pdfCargando ? <CircularProgress size={16} color="inherit"/> : <PictureAsPdfIcon />}
            disabled={pdfCargando}
            onClick={async () => {
              setPdfCargando(true); setPdfProgress(0);
              try {
                await descargarContratoCapellanesPDF(form, afiliadoId, geoParaPDF, hashContrato,
                  (cur, tot) => setPdfProgress(Math.round(cur/tot*100)));
              } finally { setPdfCargando(false); setPdfProgress(0); }
            }}
            sx={{ borderRadius:2, fontWeight:700, py:1.1, borderColor:'#2e7d32', color:'#2e7d32',
              '&:hover':{ background:'rgba(46,125,50,0.06)' } }}
          >
            {pdfCargando
              ? `Generando formato Capellanes… ${pdfProgress}%`
              : 'Descargar formato Alianza Capellanes (PDF)'}
          </Button>
          <Typography variant="caption" sx={{ display: 'block', color: '#999', mt: 1, textAlign: 'center' }}>
            No. de solicitud: <strong>#{afiliadoId ? String(afiliadoId).padStart(6,'0') : 'ASIGNADO POR EL SISTEMA'}</strong>
          </Typography>
        </Box>

        <Button variant="outlined" color="success" onClick={onClose} sx={{ borderRadius: 6, px: 4 }}>
          Cerrar
        </Button>
      </DialogContent>
    </Dialog>
  );

  const StepContent = steps[step];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper"
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 }, maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}>
      <Box sx={{ flexShrink: 0 }}>
        <LinearProgress variant="determinate" value={((step + 1) / STEPS.length) * 100}
          sx={{ height: 5, '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #2e7d32, #66bb6a)' }, background: '#e8f5e9' }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
          <Box>
            <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.7rem' }}>
              SOLICITUD DE AFILIACIÓN — PASO {step + 1} DE {STEPS.length}
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a1a', mt: 0.2 }}>{STEPS[step].label}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ color: '#999', fontWeight: 600 }}>{Math.round(((step + 1) / STEPS.length) * 100)}%</Typography>
            <IconButton size="small" onClick={onClose} sx={{ color: '#999' }}><CloseIcon fontSize="small" /></IconButton>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', px: 3, py: 1, gap: 0.5, background: '#fafafa', borderBottom: '1px solid #f0f0f0', overflowX: 'auto' }}>
          {STEPS.map((s, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, borderRadius: 4, background: i === step ? 'rgba(46,125,50,0.12)' : 'transparent', border: i === step ? '1px solid rgba(46,125,50,0.3)' : '1px solid transparent', flexShrink: 0 }}>
              {i < step ? <CheckCircleOutlineIcon sx={{ fontSize: 14, color: '#43a047' }} /> : <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: i === step ? '#2e7d32' : '#ccc' }} />}
              <Typography variant="caption" sx={{ fontSize: '0.68rem', fontWeight: i === step ? 700 : 400, color: i === step ? '#2e7d32' : i < step ? '#43a047' : '#999', whiteSpace: 'nowrap' }}>{s.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <DialogContent ref={contentRef} sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, sm: 4 }, py: 3 }}>
        <Fade in key={step} timeout={300}><Box>{steps[step]()}</Box></Fade>
      </DialogContent>
      <Box sx={{ flexShrink: 0, px: { xs: 2, sm: 4 }, py: 2, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} disabled={step === 0 || submitting} sx={{ color: '#666', '&:hover': { color: '#2e7d32' } }}>Anterior</Button>
        <Typography variant="caption" sx={{ color: '#bbb' }}>{step + 1} / {STEPS.length}</Typography>
        {(() => {
          const bloqueado = submitting || !stepListo;
          return (
            <Button variant="contained" color="success"
              disabled={bloqueado}
              endIcon={submitting ? <CircularProgress size={16} color="inherit" /> : step < 7 ? <ArrowForwardIcon /> : <DrawIcon />}
              onClick={handleNext}
              sx={{ borderRadius: 6, px: 3,
                background: bloqueado
                  ? '#bdbdbd'
                  : 'linear-gradient(135deg, #2e7d32, #43a047)',
                '&:hover': { background: bloqueado ? '#bdbdbd' : 'linear-gradient(135deg, #1b5e20, #2e7d32)' },
                cursor: bloqueado ? 'not-allowed' : 'pointer',
              }}>
              {submitting ? 'Enviando…' : step < 7 ? 'Siguiente' : 'Firmar y Enviar'}
            </Button>
          );
        })()}
      </Box>
    </Dialog>
  );
}
